import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Bars3Icon } from '@heroicons/react/24/outline';
import { Form, Link } from '@remix-run/react';

import Button from '~/components/Button';
import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import Panel from '~/components/Panel';
import Text from '~/components/Text';
import UpgradePageLink from '~/components/UpgradePageLink';
import type { NavGroup, Publication } from '~/interfaces/publication';
import type { Subscription } from '~/interfaces/subscriptions';
import getNormalizedCategory from '~/utils/getNormalizedCategory';

const SidePanelTitle = ({ text }: { text: string | React.ReactNode }) => {
  return (
    <Text as="span" type="title" theme="primary" className="text-sm font-semibold sm:block">
      {text}
    </Text>
  );
};

const SidePanelLink = ({
  path,
  text,
  onClick,
  asTitle = false,
}: {
  text: string | React.ReactNode;
  path: string;
  onClick: () => void;
  asTitle?: boolean;
}) => {
  return (
    <Link to={path} onClick={onClick}>
      <Text
        as="span"
        type="title"
        theme={asTitle ? 'primary' : 'default'}
        className={asTitle ? 'text-sm font-semibold capitalize sm:block' : 'text-sm hover:text-wt-primary'}
      >
        {text}
      </Text>
    </Link>
  );
};

interface Props {
  publication: Publication;
  isLoggedIn?: boolean;
  subscription?: Subscription;
}

const MobileSidePanel = ({ publication, isLoggedIn, subscription }: Props) => {
  const { t } = useTranslation();

  const { handleOpenModal } = useGlobalModals();

  const [open, setOpen] = useState(false);
  const { content_tags, web_template } = publication;
  const { nav_groups, enable_customize_category_ids, category_ids } = web_template;

  const isPremiumEnabled = publication.premium_enabled;
  const isReferralEnabled = publication.has_referral_program;
  const shouldShowAccountSection = isLoggedIn || isPremiumEnabled;
  const hasMultiplePublications = web_template?.multi_pub_ids?.length >= 1;
  const hasAuthorsPage = publication?.web_template?.author_ids?.length >= 1;

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div>
      <div className="flex items-center">
        <button
          type="button"
          onClick={(_e) => {
            setOpen(true);
          }}
        >
          {isLoggedIn ? (
            <div className="h-6 w-6 overflow-hidden rounded-full">
              <img className="h-6 w-6 rounded-full" src={subscription?.avatar_url} alt="The logged in user" />
            </div>
          ) : (
            <Bars3Icon className="h-6 w-6 stroke-1 text-wt-text-on-tertiary opacity-60 hover:opacity-100" />
          )}
        </button>
      </div>

      <Panel
        open={open}
        setOpen={setOpen}
        title={t('nav.mobile_title')}
        bottomChildren={
          <div className="w-full">
            {isLoggedIn ? (
              <Form method="post" action="/logout" onClick={handleClose}>
                <input
                  type="submit"
                  value={t('shared.cta.logout') || ''}
                  className="wt-button-font text-md inline-flex w-full cursor-pointer items-center justify-center rounded-md rounded-wt border border-gray-300 bg-white py-2 font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-wt-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-400"
                />
              </Form>
            ) : (
              <div className="flex w-full flex-col space-y-2 bg-white">
                <Button type="button" variant="ghost" className="shadow-none" onClick={() => handleOpenModal('login')}>
                  {t('shared.cta.login')}
                </Button>
                <Button type="button" variant="primary" onClick={() => handleOpenModal('signup')}>
                  {t('shared.cta.subscribe')}
                </Button>
              </div>
            )}
          </div>
        }
      >
        {/* Login/Logout Section */}
        <div className="space-y-2">
          {/* General Section */}
          <div className="flex flex-col">
            <SidePanelTitle text={t('nav.links.home')} />
            <SidePanelLink path={'/'} text={t('meta.titles.posts')} onClick={handleClose} />
            {hasMultiplePublications && (
              <SidePanelLink path={'/publications'} text="Newsletters" onClick={handleClose} />
            )}
            {hasAuthorsPage && <SidePanelLink path={'/authors'} text={t('author.authors')} onClick={handleClose} />}
          </div>

          {/* Account Section */}
          {shouldShowAccountSection && (
            <div className="flex flex-col">
              <SidePanelTitle text={t('nav.mobile_sections.account')} />
              <div className="mb-2 flex flex-col">
                {isPremiumEnabled && (
                  <UpgradePageLink href={publication.web_template.external_upgrade_page_href}>
                    <Text as="span" type="title" theme="default" className="text-sm hover:text-wt-primary">
                      {t('nav.links.upgrade')}
                    </Text>
                  </UpgradePageLink>
                )}
                {isLoggedIn && subscription && (
                  <SidePanelLink
                    path={`/subscribe/${subscription?.subscriber_id || ''}/manage`}
                    text={t('nav.links.manage')}
                    onClick={handleClose}
                  />
                )}
                {isReferralEnabled && subscription && (
                  <SidePanelLink
                    path={`/subscribe/${subscription?.subscriber_id || ''}/referrals`}
                    text={t('nav.links.referrals')}
                    onClick={handleClose}
                  />
                )}
              </div>
            </div>
          )}

          {/* Custom Links Section */}
          <div className="flex flex-col space-y-2">
            {nav_groups?.map((group: NavGroup) => {
              const isCategoryGroup = group.is_category_group;

              if (isCategoryGroup) {
                return (
                  <div key={group.id}>
                    <SidePanelTitle text={group.title} />

                    <div className="mb-2 flex flex-col">
                      {content_tags?.map((tag) => {
                        if (enable_customize_category_ids && !category_ids.includes(tag.id)) {
                          return null;
                        }

                        return (
                          <div key={tag.id}>
                            <SidePanelLink
                              path={`/t/${getNormalizedCategory(tag.display)}`}
                              text={tag.display}
                              onClick={handleClose}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              const navLinksLength = group?.nav_links?.length;
              if (!navLinksLength) return null;

              const hasOneLink = group?.nav_links?.length === 1;
              if (hasOneLink) {
                const link = group?.nav_links?.[0];
                const isExternalLink = link.external_link;

                return isExternalLink ? (
                  <a key={group.id} href={link.href} target="_blank" rel="nofollow noreferrer noopener">
                    <Text as="span" type="title" theme="primary" className="text-sm font-semibold capitalize sm:block">
                      {link.cta_text}
                    </Text>
                  </a>
                ) : (
                  <div key={group.id}>
                    <SidePanelLink asTitle path={`/c/${link.href}`} text={link.cta_text} onClick={handleClose} />
                  </div>
                );
              }

              return (
                <div key={group.id}>
                  <SidePanelTitle text={group.title} />
                  <div className="mb-2 flex flex-col">
                    {group?.nav_links?.map((link) => {
                      const isExternalLink = link.external_link;

                      if (isExternalLink) {
                        return (
                          <a key={link.id} href={link.href} target="_blank" rel="nofollow noreferrer noopener">
                            <Text as="span" type="title" theme="default" className="text-sm hover:text-wt-primary">
                              {link.cta_text}
                            </Text>
                          </a>
                        );
                      }

                      return (
                        <SidePanelLink
                          key={link.id}
                          path={`/c/${link.href}`}
                          text={link.cta_text}
                          onClick={handleClose}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Panel>
    </div>
  );
};

export default MobileSidePanel;
